import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaClock, FaHashtag } from 'react-icons/fa';
import '../assets/styling/Form.css';

const Form = () => {
  const handleInputClick = (inputId) => {
    document.getElementById(inputId).focus();
    document.getElementById(inputId).click();
  };

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      phone: '',
      rentalDate: '',
      startTime: '',
      endTime: '',
      totalHours: '',
      rawMaterialType: '',
      postProduction: {
        videoEditing: false,
        cutsAndAdditions: false,
        animatedGraphics: false,
        sound: {
          noiseRemoval: false,
          soundLevels: false,
          customElements: false,
        },
        logoInsertion: false,
        subtitles: '',
        customTitles: false,
      },
      specialPackages: {
        socialMediaClips: false,
        numberOfClips: '',
        podcastFragments: false,
        fragmentDuration: '',
      },
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required('Numele și prenumele sunt obligatorii'),
      email: Yup.string().email('Adresă de email invalidă').required('Email-ul este obligatoriu'),
      phone: Yup.string().required('Numărul de telefon este obligatoriu'),
      rentalDate: Yup.date().required('Data închirierii este obligatorie'),
      startTime: Yup.string().required('Ora de început este obligatorie'),
      endTime: Yup.string().required('Ora de sfârșit este obligatorie'),
      totalHours: Yup.number().required('Numărul de ore este obligatoriu'),
      rawMaterialType: Yup.string().required('Selectați tipul de material brut'),
    }),
    onSubmit: async (values) => {
      try {
        const response = await fetch('/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        if (response.ok) {
          alert('Formularul a fost trimis cu succes!');
          formik.resetForm();
        } else {
          throw new Error('Eroare la trimiterea formularului');
        }
      } catch (error) {
        alert('A apărut o eroare la trimiterea formularului. Vă rugăm să încercați din nou.');
      }
    },
  });

  return (
    <div className="form-section-container">
      <div className="form-container">
        <form onSubmit={formik.handleSubmit}>
          <div className="form-section">
            <h3>1. DATE GENERALE OBLIGATORII</h3>
            
            <div className="form-group">
              <label htmlFor="fullName">
                <FaUser className="input-icon" /> Nume și prenume:
              </label>
              <div className="input-wrapper" onClick={() => handleInputClick('fullName')}>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.fullName}
                />
              </div>
              {formik.touched.fullName && formik.errors.fullName && (
                <div className="error">{formik.errors.fullName}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                <FaEnvelope className="input-icon" /> Email:
              </label>
              <div className="input-wrapper" onClick={() => handleInputClick('email')}>
                <input
                  type="email"
                  id="email"
                  name="email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
              </div>
              {formik.touched.email && formik.errors.email && (
                <div className="error">{formik.errors.email}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phone">
                <FaPhone className="input-icon" /> Telefon:
              </label>
              <div className="input-wrapper" onClick={() => handleInputClick('phone')}>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone}
                />
              </div>
              {formik.touched.phone && formik.errors.phone && (
                <div className="error">{formik.errors.phone}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="rentalDate">
                <FaCalendarAlt className="input-icon" /> Data închiriere:
              </label>
              <div className="input-wrapper" onClick={() => handleInputClick('rentalDate')}>
                <input
                  type="date"
                  id="rentalDate"
                  name="rentalDate"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.rentalDate}
                />
              </div>
              {formik.touched.rentalDate && formik.errors.rentalDate && (
                <div className="error">{formik.errors.rentalDate}</div>
              )}
            </div>

            <div className="time-group">
              <div className="form-group">
                <label htmlFor="startTime">
                  <FaClock className="input-icon" /> De la ora:
                </label>
                <div className="input-wrapper" onClick={() => handleInputClick('startTime')}>
                  <input
                    type="time"
                    id="startTime"
                    name="startTime"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.startTime}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="endTime">
                  <FaClock className="input-icon" /> Până la ora:
                </label>
                <div className="input-wrapper" onClick={() => handleInputClick('endTime')}>
                  <input
                    type="time"
                    id="endTime"
                    name="endTime"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.endTime}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="totalHours">
                  <FaHashtag className="input-icon" /> Total ore:
                </label>
                <div className="input-wrapper" onClick={() => handleInputClick('totalHours')}>
                  <input
                    type="number"
                    id="totalHours"
                    name="totalHours"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.totalHours}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>2. TIP MATERIAL BRUT</h3>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="threeCameras"
                  name="rawMaterialType"
                  value="3cameras"
                  onChange={formik.handleChange}
                  checked={formik.values.rawMaterialType === '3cameras'}
                />
                <label htmlFor="threeCameras">
                  Material brut din 3 camere (3 fișiere video separate + sunet)
                </label>
              </div>

              <div className="radio-option">
                <input
                  type="radio"
                  id="oneCamera"
                  name="rawMaterialType"
                  value="1camera"
                  onChange={formik.handleChange}
                  checked={formik.values.rawMaterialType === '1camera'}
                />
                <label htmlFor="oneCamera">
                  Material brut din 1 cameră (1 fișier video + sunet)
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>3. POSTPRODUCȚIE (Opțional)</h3>
            <div className="checkbox-group">
              <div className="checkbox-option">
                <input
                  type="checkbox"
                  id="videoEditing"
                  name="postProduction.videoEditing"
                  onChange={formik.handleChange}
                  checked={formik.values.postProduction.videoEditing}
                />
                <label htmlFor="videoEditing">
                  Montaj video (editare până la versiunea finală)
                </label>
              </div>

              <div className="checkbox-option">
                <input
                  type="checkbox"
                  id="cutsAndAdditions"
                  name="postProduction.cutsAndAdditions"
                  onChange={formik.handleChange}
                  checked={formik.values.postProduction.cutsAndAdditions}
                />
                <label htmlFor="cutsAndAdditions">
                  Tăieri și adăugiri
                </label>
              </div>

              <div className="checkbox-option">
                <input
                  type="checkbox"
                  id="animatedGraphics"
                  name="postProduction.animatedGraphics"
                  onChange={formik.handleChange}
                  checked={formik.values.postProduction.animatedGraphics}
                />
                <label htmlFor="animatedGraphics">
                  Grafică și titluri animate
                </label>
              </div>

              <div className="sound-options">
                <h4>Sunet:</h4>
                <div className="checkbox-option">
                  <input
                    type="checkbox"
                    id="noiseRemoval"
                    name="postProduction.sound.noiseRemoval"
                    onChange={formik.handleChange}
                    checked={formik.values.postProduction.sound.noiseRemoval}
                  />
                  <label htmlFor="noiseRemoval">
                    Eliminare zgomote nedorite
                  </label>
                </div>

                <div className="checkbox-option">
                  <input
                    type="checkbox"
                    id="soundLevels"
                    name="postProduction.sound.soundLevels"
                    onChange={formik.handleChange}
                    checked={formik.values.postProduction.sound.soundLevels}
                  />
                  <label htmlFor="soundLevels">
                    Ajustare niveluri sonore
                  </label>
                </div>

                <div className="checkbox-option">
                  <input
                    type="checkbox"
                    id="customElements"
                    name="postProduction.sound.customElements"
                    onChange={formik.handleChange}
                    checked={formik.values.postProduction.sound.customElements}
                  />
                  <label htmlFor="customElements">
                    Elemente personalizate
                  </label>
                </div>
              </div>

              <div className="checkbox-option">
                <input
                  type="checkbox"
                  id="logoInsertion"
                  name="postProduction.logoInsertion"
                  onChange={formik.handleChange}
                  checked={formik.values.postProduction.logoInsertion}
                />
                <label htmlFor="logoInsertion">
                  Inserare logo-uri
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="subtitles">Subtitrări în limba:</label>
                <input
                  type="text"
                  id="subtitles"
                  name="postProduction.subtitles"
                  onChange={formik.handleChange}
                  value={formik.values.postProduction.subtitles}
                />
              </div>

              <div className="checkbox-option">
                <input
                  type="checkbox"
                  id="customTitles"
                  name="postProduction.customTitles"
                  onChange={formik.handleChange}
                  checked={formik.values.postProduction.customTitles}
                />
                <label htmlFor="customTitles">
                  Titluri custom
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>4. PACHETE SPECIALE (Opțional)</h3>
            <div className="special-packages">
              <div className="package-option">
                <div className="checkbox-option">
                  <input
                    type="checkbox"
                    id="socialMediaClips"
                    name="specialPackages.socialMediaClips"
                    onChange={formik.handleChange}
                    checked={formik.values.specialPackages.socialMediaClips}
                  />
                  <label htmlFor="socialMediaClips">
                    Clipuri short social media
                  </label>
                </div>
                {formik.values.specialPackages.socialMediaClips && (
                  <div className="form-group">
                    <label htmlFor="numberOfClips">Număr de clipuri:</label>
                    <input
                      type="number"
                      id="numberOfClips"
                      name="specialPackages.numberOfClips"
                      onChange={formik.handleChange}
                      value={formik.values.specialPackages.numberOfClips}
                    />
                  </div>
                )}
              </div>

              <div className="package-option">
                <div className="checkbox-option">
                  <input
                    type="checkbox"
                    id="podcastFragments"
                    name="specialPackages.podcastFragments"
                    onChange={formik.handleChange}
                    checked={formik.values.specialPackages.podcastFragments}
                  />
                  <label htmlFor="podcastFragments">
                    Fragmente de podcast pentru promovare
                  </label>
                </div>
                {formik.values.specialPackages.podcastFragments && (
                  <div className="form-group">
                    <label htmlFor="fragmentDuration">Durată fragment:</label>
                    <input
                      type="text"
                      id="fragmentDuration"
                      name="specialPackages.fragmentDuration"
                      onChange={formik.handleChange}
                      value={formik.values.specialPackages.fragmentDuration}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-button">
              Trimite Formularul
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form;
